/**
 * Which tabs Hotel Optimisation shows, and which one it opens on.
 *
 * ## Why this is a module and not six lines in the page
 *
 * Block Manager used to be its own page under the **Core** permission group.
 * When it became a tab here it kept its `block-manager` key, while the page
 * around it is gated on the **Ops** `hotel-optimisation` key. So the route has
 * to admit anyone holding EITHER key — otherwise the move would have silently
 * taken Block Manager away from every Core user who had it.
 *
 * That leaves an invariant spanning two files: the router decides who gets
 * through the door, the page decides what they then see. If those two lists
 * disagree, someone gets in and is shown nothing, or is bounced from a page
 * that has a tab for them. Both now read `HOTEL_OPTIMISATION_TAB_KEYS` from
 * here, so they cannot drift.
 */

export type OptimisationTab =
  | 'release'
  | 'space-confirmations'
  | 'event-release'
  | 'actions'
  | 'wasting'
  | 'forecast'
  | 'block-manager'

export const OPTIMISATION_TABS: OptimisationTab[] = [
  'release',
  'space-confirmations',
  'event-release',
  'actions',
  'wasting',
  'forecast',
  'block-manager',
]

/**
 * The permission keys that grant entry to /hotel-optimisation.
 *
 * Read by the router guard (the any-of rule, like administration and
 * property-setup) and by `visibleTabs` below.
 */
export const HOTEL_OPTIMISATION_TAB_KEYS = ['hotel-optimisation', 'block-manager'] as const

/** The permission key a tab belongs to. */
export function tabPermissionKey(tab: OptimisationTab): string {
  return tab === 'block-manager' ? 'block-manager' : 'hotel-optimisation'
}

export type CanAccess = (pageKey: string) => boolean

export function tabVisible(tab: OptimisationTab, canAccess: CanAccess): boolean {
  return canAccess(tabPermissionKey(tab))
}

export function visibleTabs(canAccess: CanAccess): OptimisationTab[] {
  return OPTIMISATION_TABS.filter(tab => tabVisible(tab, canAccess))
}

/**
 * The tab to open: the `?tab=` one when it exists AND the viewer may see it,
 * otherwise their first visible tab.
 *
 * A deep link to a forbidden tab falls back rather than rendering an empty
 * page — the dashboard's release widget links straight to `?tab=release`, and
 * a Core user following that link must still land somewhere real.
 */
export function resolveTab(queryTab: unknown, canAccess: CanAccess): OptimisationTab {
  const q = String(queryTab ?? '')
  const requested = (OPTIMISATION_TABS as string[]).includes(q) ? (q as OptimisationTab) : null
  if (requested && tabVisible(requested, canAccess)) return requested
  // 'release' is the page's primary view; OPTIMISATION_TABS order decides the
  // rest, so a block-manager-only viewer lands on Block Manager.
  return visibleTabs(canAccess)[0] ?? 'release'
}
